import React, { useState, useRef, useLayoutEffect, useEffect, useCallback } from "react";
import styles from "./ExpandableCapsules.module.css";

/**
 * ExpandableCapsules
 * Renders a list of capsule/badge elements.
 * If the capsules wrap past line 1, it truncates line 1 with a `+{n}` badge.
 * Clicking `+{n}` expands to show all capsules.
 * When expanded, a `− Less` badge allows collapsing back to line 1.
 */
export default function ExpandableCapsules({
  items = [],
  renderItem,
  emptyLabel = "None",
  emptyClassName = "",
  containerClassName = "",
  containerStyle = {},
  variant = "default", // "default" or "property"
  plusClassName = "",
  collapseClassName = "",
}) {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(items.length);
  const [needsCollapse, setNeedsCollapse] = useState(false);

  const calculateFit = useCallback(() => {
    if (!measureRef.current || !items.length) {
      setNeedsCollapse(false);
      setVisibleCount(items.length);
      return;
    }

    const containerWidth = measureRef.current.clientWidth;
    if (containerWidth <= 0) return;

    const children = Array.from(measureRef.current.children);
    if (!children.length) {
      setNeedsCollapse(false);
      setVisibleCount(items.length);
      return;
    }

    const firstTop = children[0].offsetTop;

    // Count how many children reside strictly on line 1
    let line1Count = 0;
    for (let i = 0; i < children.length; i++) {
      if (Math.abs(children[i].offsetTop - firstTop) <= 6) {
        line1Count++;
      } else {
        break;
      }
    }

    // If all children fit on line 1, no truncation needed
    if (line1Count >= children.length) {
      setNeedsCollapse(false);
      setVisibleCount(children.length);
      return;
    }

    // Some children wrap to line 2.
    // Determine how many fit alongside the `+{n}` pill on line 1.
    const plusButtonWidth = 48; // approximate width of the +{n} button + gap
    let fitCount = line1Count;

    while (fitCount > 1) {
      const lastChild = children[fitCount - 1];
      const rightEdge = lastChild.offsetLeft + lastChild.offsetWidth + 8 + plusButtonWidth;
      if (rightEdge <= containerWidth) {
        break;
      }
      fitCount--;
    }

    setNeedsCollapse(true);
    setVisibleCount(Math.max(1, fitCount));
  }, [items]);

  useLayoutEffect(() => {
    calculateFit();
  }, [calculateFit]);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(() => {
      calculateFit();
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [calculateFit]);

  if (!items.length) {
    return (
      <div className={[styles.tagsContainer, containerClassName].join(" ")} style={containerStyle}>
        <span className={emptyClassName}>{emptyLabel}</span>
      </div>
    );
  }

  const defaultBtnClass = variant === "property"
    ? [styles.expandBtn, styles.propertyExpandBtn].join(" ")
    : styles.expandBtn;

  const hiddenCount = items.length - visibleCount;

  return (
    <div ref={containerRef} className={styles.wrapper}>
      {/* Invisible container used to accurately measure line 1 wrapping without visual jitter */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className={[styles.tagsContainer, containerClassName].join(" ")}
        style={{
          ...containerStyle,
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          visibility: "hidden",
          pointerEvents: "none",
          zIndex: -999,
          margin: 0,
        }}
      >
        {items.map((item, idx) => (
          <React.Fragment key={idx}>
            {renderItem(item, idx)}
          </React.Fragment>
        ))}
      </div>

      {/* Visible interactive container */}
      <div className={[styles.tagsContainer, containerClassName].join(" ")} style={containerStyle}>
        {(!needsCollapse || isExpanded) ? (
          <>
            {items.map((item, idx) => (
              <React.Fragment key={idx}>
                {renderItem(item, idx)}
              </React.Fragment>
            ))}
            {needsCollapse && isExpanded && (
              <button
                type="button"
                className={[defaultBtnClass, collapseClassName].filter(Boolean).join(" ")}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(false);
                }}
                title="Show less"
              >
                − Less
              </button>
            )}
          </>
        ) : (
          <>
            {items.slice(0, visibleCount).map((item, idx) => (
              <React.Fragment key={idx}>
                {renderItem(item, idx)}
              </React.Fragment>
            ))}
            {hiddenCount > 0 && (
              <button
                type="button"
                className={[defaultBtnClass, plusClassName].filter(Boolean).join(" ")}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(true);
                }}
                title={`Show ${hiddenCount} more`}
              >
                +{hiddenCount}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
